"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  CalendarDays,
  LayoutDashboard,
  LayoutList,
  ClipboardCheck,
  Footprints,
  ListChecks,
  LogOut,
  LogIn,
  Sparkles,
  ChevronsUpDown,
  Building2,
  Users,
  MessageSquare,
  BrushCleaning,
  Link2,
  Wrench,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { createClient } from "@/utils/supabase/client";
import { Logo } from "@/components/logo";
import { preloadSettings } from "@/lib/settings-cache";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

type NavLink = { href: string; label: string; icon: React.ComponentType<{ className?: string }> };

const OPERATIONS: NavLink[] = [
  { href: "/",             label: "Overview",     icon: LayoutDashboard },
  { href: "/calendar",     label: "Calendar",     icon: CalendarDays },
  { href: "/reservations", label: "Reservations", icon: LayoutList },
  { href: "/reports",      label: "Cleaning reports", icon: ClipboardCheck },
  { href: "/walkthroughs", label: "Walkthroughs", icon: Footprints },
  { href: "/tasks",        label: "Tasks",        icon: ListChecks },
];

const SETTINGS_CHILDREN: NavLink[] = [
  { href: "/settings/properties",    label: "Properties",    icon: Building2     },
  { href: "/settings/contacts",      label: "Contacts",      icon: Users         },
  { href: "/settings/notifications", label: "Notifications", icon: MessageSquare },
  { href: "/settings/cleaning",      label: "Cleaning",      icon: BrushCleaning },
  { href: "/settings/integrations",  label: "Integrations",  icon: Link2         },
  { href: "/settings/walkthroughs",  label: "Walkthroughs",  icon: Footprints    },
  { href: "/settings/system",        label: "System",        icon: Wrench        },
];

export function AppSidebar({ user: initialUser }: { user: User | null }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const [user, setUser] = React.useState<User | null>(initialUser);

  // Warm the settings cache for instant Notifications/Cleaning loads
  React.useEffect(() => { if (initialUser) preloadSettings(); }, [initialUser]);

  React.useEffect(() => {
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function signOut() {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push("/");
    router.refresh();
  }

  const renderNav = (items: NavLink[]) =>
    items.map(({ href, label, icon: Icon }) => {
      const active = pathname === href || (href !== "/" && pathname.startsWith(href));
      return (
        <SidebarMenuItem key={href}>
          <SidebarMenuButton asChild isActive={active} tooltip={label}>
            <Link href={href} onClick={() => { if (isMobile) setOpenMobile(false); }}>
              <Icon className="size-4" />
              <span>{label}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });

  const initials = (user?.email ?? "U")
    .split("@")[0]
    .slice(0, 2)
    .toUpperCase();

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className="h-12 flex-row items-center justify-center px-2 group-data-[collapsible=icon]:px-0">
        <Link
          href="/"
          aria-label="XCORP"
          onClick={() => { if (isMobile) setOpenMobile(false); }}
          className="flex h-8 w-full items-center justify-start gap-2 rounded-md px-2 transition-colors hover:bg-sidebar-accent group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        >
          <Logo markHeight={22} wordmarkClassName="group-data-[collapsible=icon]:hidden" />
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Operations</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{renderNav(user ? OPERATIONS : OPERATIONS.filter((l) => l.href === "/calendar"))}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {user && (
          <SidebarGroup>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{renderNav(SETTINGS_CHILDREN)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
                    <Avatar className="size-8 rounded-lg">
                      <AvatarFallback className="rounded-lg text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user.email?.split("@")[0] ?? "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="grid gap-0.5">
                      <span className="text-sm font-medium">{user.email?.split("@")[0]}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link href="/changelog" onClick={() => { if (isMobile) setOpenMobile(false); }}>
                      <Sparkles className="size-4" />
                      What&apos;s new
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut}>
                    <LogOut className="size-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarMenuButton asChild tooltip="Sign in">
                <Link href="/login" onClick={() => { if (isMobile) setOpenMobile(false); }}>
                  <LogIn className="size-4" />
                  <span>Sign in</span>
                </Link>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
